import {
  AdminInput,
  AdminSpinner,
  Disclaimer,
  Div,
  DropdownMenuComponent,
  H5,
  Icon,
  Img,
  LocationPinIcon,
  Overlay,
  P,
  Radio,
  getAllVerifiedLocations,
  isAdmin,
  lightTheme,
  overlayAnimationVariant,
  restaurantImage,
  useDisableBodyScroll,
  reportError,
} from '@vgrubs/components';
import { getPartnerLocations } from 'components/src/api/partner/locations.partner-api';
import {
  SimpleLocationEntity,
  SimpleLocationWithTodayDataResponse,
} from 'components/src/types/location.types';
import 'flatpickr/dist/flatpickr.css';
import { AnimatePresence } from 'framer-motion';
import debouce from 'lodash.debounce';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  LocationsPicker,
  ScrollableWrapper,
} from './AdminLocationsPickerStyled';

export const AdminSingleLocationPicker = ({
  selectedLocation,
  setSelectedLocation,
  handleSubmit,
  customLocations = null,
  onFetchedLocations,
}: {
  selectedLocation: SimpleLocationEntity | null;
  setSelectedLocation:
    | Dispatch<SetStateAction<SimpleLocationEntity | null>>
    | ((location: SimpleLocationEntity | null) => void);
  handleSubmit: (location: SimpleLocationEntity) => void;
  customLocations?: SimpleLocationEntity[] | null;
  onFetchedLocations?: (location: SimpleLocationEntity) => void;
}) => {
  const [dropdown, setDropdown] = useState(false);
  const [data, setData] = useState<SimpleLocationEntity[]>([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [ref, inView] = useInView();
  const [hasMore, setHasMore] = useState(true);
  const [isInit, setIsInit] = useState(true);

  useDisableBodyScroll(dropdown);

  async function getLocations(
    search: boolean,
    page: number,
    hasMore: boolean,
    query: string
  ) {
    if (customLocations && customLocations?.length > 0) {
      setData(customLocations);
      setSelectedLocation(customLocations[0]);
      return;
    }
    if (!hasMore) return;
    try {
      setLoading(true);

      const params = isAdmin
        ? {
            query,
            page,
            limit: 10,
          }
        : {
            query,
            page,
            limit: 10,
            verified: true,
          };

      const getFunction = isAdmin
        ? getAllVerifiedLocations
        : getPartnerLocations;

      const { data } = await getFunction(params);

      const dataLocations = isAdmin
        ? (data.data as SimpleLocationEntity[])
        : (data.data as SimpleLocationWithTodayDataResponse[]).map(
            ({ location }) => location
          );

      if (!search) {
        if (dataLocations[0]) {
          setHasMore(true);
          setData((prevItems) => [...prevItems, ...dataLocations]);
        } else {
          setHasMore(false);
        }
      } else {
        if (dataLocations[0]) {
          setHasMore(true);
          setData(() => [...dataLocations]);
        } else {
          setData(() => [...dataLocations]);
          setHasMore(false);
        }
      }

      if (dataLocations.length > 0 && isInit && selectedLocation === null) {
        const location = dataLocations[0];
        setSelectedLocation(location);
        handleSubmit(location);
        setIsInit(false);
      }

      setPage((prevPage) => prevPage + 1);
    } catch (error) {
      setHasMore(false);
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (data.length < 1) {
      getLocations(false, page, hasMore, query);
    }
  }, []);

  useEffect(() => {
    if (!dropdown && query) {
      setQuery('');
      setPage(1);
      setHasMore(true);
      getLocations(true, 1, true, '');
    }
  }, [dropdown]);

  useEffect(() => {
    if (inView && data.length > 0) {
      getLocations(false, page, hasMore, query);
    }
  }, [inView]);

  const handleSelectLocation = (newItem: SimpleLocationEntity) => {
    setSelectedLocation(newItem);
    setDropdown((prev) => !prev);
    handleSubmit(newItem as SimpleLocationEntity);
  };

  function isLocationSelected(item: SimpleLocationEntity) {
    return selectedLocation?.id === item.id;
  }

  function renderLocationInputPlaceholder() {
    if (!selectedLocation) {
      return 'Select Location';
    } else if (selectedLocation) {
      return selectedLocation?.name;
    }
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasMore(true);
    setPage(1);
    setQuery(e.target.value);
    getLocations(true, 1, true, e.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 500);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  return (
    <AnimatePresence>
      <Div $relative $height="40px" $heightSM="40px">
        <LocationsPicker
          $opened={dropdown}
          onClick={() => setDropdown((prev) => !prev)}
          aria-haspopup="dialog"
          aria-expanded={dropdown}
          aria-label="Select location"
        >
          <Div>{renderLocationInputPlaceholder()}</Div>

          <Icon $opacity="0.5">
            <LocationPinIcon />
          </Icon>
        </LocationsPicker>

        <DropdownMenuComponent
          isOpen={dropdown}
          maxHeight="70vh"
          padding="0"
          fullWidth
        >
          {loading && <AdminSpinner absolute small />}

          <Div $p="20px">
            <AdminInput
              search
              onChange={debouncedResults}
              placeholder="Search Location"
            />
          </Div>
          <ScrollableWrapper>
            {data.length === 0 && !hasMore && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img
                  src={restaurantImage}
                  alt="restaurant"
                  $width="250px"
                  $widthSM="150px"
                />
                <H5>No Locations</H5>
              </Div>
            )}
            {data.map((l, i) => {
              if (data.length === i + 1) {
                return (
                  <Div
                    ref={ref}
                    key={i}
                    $box
                    $p="12px 20px"
                    $mb="8"
                    $flex
                    $alignCenter
                    $between
                    onClick={() => handleSelectLocation(l)}
                    $gap="16px"
                    $pointer
                  >
                    <div>
                      <P
                        $bold
                        $mb="8"
                        $color={isLocationSelected(l) && lightTheme.primary}
                        $lineHeight="1.2"
                      >
                        {l.name}
                      </P>
                      <Disclaimer $mb="0">{l.address}</Disclaimer>
                    </div>

                    <Radio checked={isLocationSelected(l)} />
                  </Div>
                );
              } else
                return (
                  <Div
                    key={i}
                    $box
                    $p="12px 20px"
                    $mb="8"
                    $flex
                    $alignCenter
                    $between
                    onClick={() => handleSelectLocation(l)}
                    $gap="16px"
                    $pointer
                  >
                    <div>
                      <P
                        $bold
                        $mb="8"
                        $color={isLocationSelected(l) && lightTheme.primary}
                        $lineHeight="1.2"
                      >
                        {l.name}
                      </P>
                      <Disclaimer $mb="0">{l.address}</Disclaimer>
                    </div>

                    <Radio checked={isLocationSelected(l)} />
                  </Div>
                );
            })}
            <div></div>
          </ScrollableWrapper>
        </DropdownMenuComponent>
      </Div>

      {dropdown && (
        <Overlay
          key="modalOverlay"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setDropdown(false)}
        />
      )}
    </AnimatePresence>
  );
};
